import { createCutGlueURL } from '@/api'
import { components as batteryComponents } from '@/battery-types'
import { steeringAuthInit } from '@/stores/auth'
import { Episode, Recording } from '@/types'
import { aggregateWithIdsParameter, useListHelpers, useQuery, useRetrieveHelpers } from '@/util'
import {
  APIListPaginated,
  APIRemove,
  APIRetrieve,
  APIUpdate,
  createExtendableAPI,
  useErrorList,
} from '@rokoli/bnb/drf'
import { defineStore } from 'pinia'
import { computed, MaybeRefOrGetter, toValue } from 'vue'

export const useRecordingStore = defineStore('recordings', () => {
  const { api, base } = createExtendableAPI<Recording>(
    createCutGlueURL.prefix('recordings'),
    steeringAuthInit,
  )
  const listOperations = APIListPaginated(api)
  const retrieveOperations = APIRetrieve(api, {
    aggregate: aggregateWithIdsParameter(listOperations.listIsolated),
  })

  const { partialUpdate } = APIUpdate<
    Recording,
    Recording,
    batteryComponents['schemas']['PatchedUpdateRecordingRequest']
  >(api)

  return {
    ...base,
    ...listOperations,
    ...retrieveOperations,
    ...APIRemove(api),
    partialUpdate,
    ...useRetrieveHelpers({ ...base, ...retrieveOperations }),
    ...useListHelpers({ ...base, ...listOperations }),
  }
})

export function useEpisodeRecording(episodeId: MaybeRefOrGetter<Episode['id'] | null | undefined>) {
  const recordingStore = useRecordingStore()
  const query = useQuery(() => ({ episodeIds: toValue(episodeId) }))
  const { result, isLoading, reload, error } = recordingStore.paginate(1, 1, { query })
  const recording = computed(() => {
    const id = toValue(episodeId)
    return result.value.items.find((r) => r.episodeId === id) ?? null
  })
  return { recording, isLoading, reload, errors: useErrorList(error) }
}
